import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/subscriptionService';
import { AdminUser, SubscriptionStatus } from '../../types';
import { GrantSubscriptionDialog } from './components/GrantSubscriptionDialog';
import { BanUserDialog } from './components/BanUserDialog';

const statusColor = (status?: SubscriptionStatus | null) => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
    case SubscriptionStatus.ADMIN_GRANT:
      return 'success';
    case SubscriptionStatus.TRIALING:
      return 'info';
    case SubscriptionStatus.PAST_DUE:
      return 'warning';
    case SubscriptionStatus.CANCELED:
    case SubscriptionStatus.UNPAID:
    case SubscriptionStatus.BANNED:
      return 'error';
    case SubscriptionStatus.PENDING_TRIAL:
      return 'default';
    default:
      return 'default';
  }
};

export const AdminUsersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState<'activity' | 'requests' | ''>('');
  const [grantDialog, setGrantDialog] = useState<AdminUser | null>(null);
  const [banDialog, setBanDialog] = useState<AdminUser | null>(null);

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users', search, filterStatus, sortBy],
    queryFn: () =>
      adminService.listUsers({
        search: search || undefined,
        sub_status: filterStatus || undefined,
        sort_by: sortBy || undefined,
        size: 100,
      }),
  });

  const startTrialMutation = useMutation({
    mutationFn: (userId: string) => adminService.startTrial(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const revokeTrialMutation = useMutation({
    mutationFn: (userId: string) => adminService.revokeTrial(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const unbanMutation = useMutation({
    mutationFn: (userId: string) => adminService.unbanUser(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        {t('admin.users.title')}
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder={t('admin.users.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <Select
          size="small"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('admin.users.allStatuses')}</MenuItem>
          {Object.values(SubscriptionStatus).map((s) => (
            <MenuItem key={s} value={s}>
              {t(`subscription.status.${s}`)}
            </MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as any)}
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">{t('admin.users.sortDefault')}</MenuItem>
          <MenuItem value="activity">{t('admin.users.sortByActivity')}</MenuItem>
          <MenuItem value="requests">{t('admin.users.sortByRequests')}</MenuItem>
        </Select>
      </Box>

      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">{t('admin.loadError')}</Alert>}

      {users && (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.users.name')}</TableCell>
                <TableCell>{t('admin.users.email')}</TableCell>
                <TableCell>{t('admin.users.subscriptionStatus')}</TableCell>
                <TableCell>{t('admin.users.totalRequests')}</TableCell>
                <TableCell>{t('admin.users.lastActive')}</TableCell>
                <TableCell align="right">{t('admin.users.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{ opacity: user.is_banned ? 0.6 : 1 }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {user.first_name} {user.last_name}
                    </Typography>
                    {user.is_banned && (
                      <Chip label={t('admin.users.banned')} size="small" color="error" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  <TableCell>
                    {user.subscription ? (
                      <Chip
                        label={t(`subscription.status.${user.subscription.status}`)}
                        color={statusColor(user.subscription.status) as any}
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={user.total_ai_requests > 100 ? 'warning.main' : 'text.primary'} fontWeight={user.total_ai_requests > 100 ? 600 : 400}>
                      {user.total_ai_requests}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.last_active_at
                        ? new Date(user.last_active_at).toLocaleDateString('fr-FR')
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('admin.users.viewDetail')}>
                      <IconButton size="small" onClick={() => navigate(`/admin/users/${user.id}`)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {user.subscription?.status === SubscriptionStatus.PENDING_TRIAL && (
                      <Tooltip title={t('admin.users.startTrial')}>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => startTrialMutation.mutate(user.id)}
                          disabled={startTrialMutation.isPending}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    {user.subscription?.status === SubscriptionStatus.TRIALING && (
                      <Tooltip title={t('admin.users.revokeTrial')}>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => revokeTrialMutation.mutate(user.id)}
                          disabled={revokeTrialMutation.isPending}
                        >
                          <TimerOffIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title={t('admin.users.grantSubscription')}>
                      <IconButton size="small" color="success" onClick={() => setGrantDialog(user)}>
                        <CardGiftcardIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {user.is_banned ? (
                      <Tooltip title={t('admin.users.unban')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => unbanMutation.mutate(user.id)}
                          disabled={unbanMutation.isPending}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('admin.users.ban')}>
                        <IconButton size="small" color="error" onClick={() => setBanDialog(user)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {grantDialog && (
        <GrantSubscriptionDialog
          user={grantDialog}
          open
          onClose={() => setGrantDialog(null)}
          onSuccess={() => {
            setGrantDialog(null);
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}

      {banDialog && (
        <BanUserDialog
          user={banDialog}
          open
          onClose={() => setBanDialog(null)}
          onSuccess={() => {
            setBanDialog(null);
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}
    </Box>
  );
};
